import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { Sop } from "@/types/db";
import { VideoPlayer } from "@/components/VideoPlayer";
import { AutoText } from "@/components/RichText";

export function SOPs() {
  const [sops, setSops] = useState<Sop[]>([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [open, setOpen] = useState<Sop | null>(null);

  useEffect(() => { void load(); }, []);

  async function load() {
    const { data } = await supabase.from("hq_sops").select("*").order("title");
    setSops((data as Sop[]) ?? []);
  }

  const cats = Array.from(new Set(sops.map(s => s.category).filter(Boolean))).sort() as string[];
  const filtered = useMemo(() => {
    const list = sops.filter(s => {
      if (cat !== "all" && s.category !== cat) return false;
      if (q && !(`${s.title} ${s.description ?? ""}`.toLowerCase().includes(q.toLowerCase()))) return false;
      return true;
    });
    // Pinned first, then alphabetical (already title-sorted from DB).
    return list.slice().sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      const sa = a.sort_order ?? Number.MAX_SAFE_INTEGER;
      const sb = b.sort_order ?? Number.MAX_SAFE_INTEGER;
      if (sa !== sb) return sa - sb;
      return a.title.localeCompare(b.title);
    });
  }, [sops, q, cat]);

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">SOP Library</h1>

      <div className="card">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <input className="input max-w-sm" placeholder="Search SOPs…" value={q} onChange={e => setQ(e.target.value)} />
          <select className="input max-w-[200px]" value={cat} onChange={e => setCat(e.target.value)}>
            <option value="all">All categories</option>
            {cats.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {filtered.length === 0 ? (
          <p className="py-10 text-center text-sm text-gray2">No SOPs match.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map(s => {
              const hasVideo = !!(s.video_url || s.video_storage_path);
              return (
                <button
                  key={s.id}
                  onClick={() => setOpen(s)}
                  className="rounded-xl ring-1 ring-line bg-white p-4 text-left transition hover:shadow-soft hover:ring-teal"
                >
                  <div className="mb-2 flex flex-wrap items-center gap-1">
                    {s.pinned && <span className="pill pill-warn">📌 Pinned</span>}
                    {s.category && <span className="pill pill-mint">{s.category}</span>}
                    {hasVideo && <span className="pill pill-mint">📹 Video</span>}
                    {s.estimated_minutes != null && (
                      <span className="pill pill-mint">{s.estimated_minutes} min</span>
                    )}
                  </div>
                  <h3 className="text-base font-semibold">{s.title}</h3>
                  {s.description && <p className="mt-1 line-clamp-3 text-sm text-gray2">{s.description}</p>}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/60 p-4" onClick={() => setOpen(null)}>
          <div className="max-h-[90vh] w-full max-w-3xl overflow-auto rounded-xl bg-white p-6 shadow-soft" onClick={e => e.stopPropagation()}>
            <div className="mb-4 flex items-start justify-between">
              <div>
                <div className="mb-2 flex flex-wrap items-center gap-1">
                  {open.pinned && <span className="pill pill-warn">📌 Pinned</span>}
                  {open.category && <span className="pill pill-mint">{open.category}</span>}
                  {open.estimated_minutes != null && (
                    <span className="pill pill-mint">📹 {open.estimated_minutes} min</span>
                  )}
                </div>
                <h2 className="text-xl font-bold">{open.title}</h2>
              </div>
              <button onClick={() => setOpen(null)} className="text-2xl text-gray2 hover:text-ink">×</button>
            </div>

            {(open.video_url || open.video_storage_path) && (
              <div className="mb-4">
                <VideoPlayer url={open.video_url} storagePath={open.video_storage_path} />
              </div>
            )}

            {open.description && <p className="mb-4 text-sm text-gray2">{open.description}</p>}
            {open.document_url && (
              <a href={open.document_url} target="_blank" rel="noreferrer" className="btn mb-4 inline-block">↗ Open document</a>
            )}
            {open.content && (
              <AutoText text={open.content} className="prose prose-sm max-w-none text-sm" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
